import type { Dispatch, FC, SetStateAction } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import {
  Box,
  IconButton,
  Snackbar,
  SnackbarContent,
  Stack,
  Typography,
} from '@mui/material';

import { ReportType } from 'src/constants/report';
import useReportConfigMaps from 'src/components/reportConfig/hooks/useReportConfigMaps';

import { styles } from './reportModalSnackbar.styles';

export interface ReportModalSnackbarProps {
  unavailableReportTypes: ReportType[];
  setUnavailableReportTypes: Dispatch<SetStateAction<ReportType[]>>;
}

const ReportModalSnackbar: FC<ReportModalSnackbarProps> = ({
  setUnavailableReportTypes,
  unavailableReportTypes,
}) => {
  const { t } = useTranslation(['dashboard', 'report']);

  const { reportNamesPerReportType } = useReportConfigMaps();

  const unavailableReportsByCase = useMemo(() => {
    const unavailableOpenCommentReports = unavailableReportTypes.filter(
      (reportType) => reportType === ReportType.OPENCOMMENT,
    );
    const unavailableNonCommentReports = unavailableReportTypes.filter(
      (reportType) => reportType !== ReportType.OPENCOMMENT,
    );

    return {
      OpenCommentReport: unavailableOpenCommentReports,
      Default: unavailableNonCommentReports,
    };
  }, [unavailableReportTypes]);

  return (
    <Snackbar
      autoHideDuration={6000}
      onClose={() => setUnavailableReportTypes([])}
      open={!!unavailableReportTypes.length}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={styles.reportSnackbar}
    >
      <SnackbarContent
        sx={styles.reportSnackbarContent}
        message={
          <Stack direction="row">
            <Stack
              direction="column"
              sx={{
                justifyContent: 'center',
              }}
            >
              <Stack sx={styles.reportSnackbarIcon}>
                <ErrorRounded />
              </Stack>
            </Stack>
            <Stack sx={styles.reportSnackbarText}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px',
                  lineHeight: '1.75rem',
                }}
              >
                {t('downloadManager:DownloadManager')}
              </Typography>
              <Stack
                sx={{
                  gap: 3,
                }}
              >
                {Object.entries(unavailableReportsByCase).map(
                  ([caseName, reports], j) =>
                    reports.length > 0 ? (
                      <Box key={j}>
                        <span>
                          {t(`ButtonReportModal.FilesNotAvailable.${caseName}`)}
                        </span>
                        <Box
                          sx={{
                            maxWidth: '100%',
                          }}
                        >
                          {reports.map((reportType, i, total) => (
                            <Box
                              key={i}
                              title={
                                reportNamesPerReportType?.[reportType] ?? ''
                              }
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {reportNamesPerReportType?.[reportType] ?? ''}
                              {i + 1 < total.length && <>,&nbsp;</>}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null,
                )}
              </Stack>
            </Stack>
          </Stack>
        }
        action={
          <IconButton
            size="large"
            aria-label="close"
            color="primary"
            onClick={() => setUnavailableReportTypes([])}
          >
            <CloseRounded color="primary" />
          </IconButton>
        }
      />
    </Snackbar>
  );
};

export default ReportModalSnackbar;
